import { useEffect } from 'react';

type SeoOptions = {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  /** 分享图 URL（站内相对路径或绝对 URL），默认 /og-cover.png */
  image?: string;
  /** 为 true 时输出 noindex（例如 404 页面） */
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

const SITE_NAME = 'Forex Academy Thailand';
const DEFAULT_COVER = '/og-cover.png';
const FALLBACK_ORIGIN = 'https://forexnara.com';

/** SSR 安全地获取站点 origin（服务端渲染时使用预渲染注入的 origin） */
export function siteOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return (
    (globalThis as { __SITE_ORIGIN__?: string }).__SITE_ORIGIN__ ?? FALLBACK_ORIGIN
  );
}

/** 将站内路径转换为绝对 URL（与 canonical 生成逻辑保持一致） */
export function siteUrl(path = '/') {
  const base =
    typeof __BASE_PATH__ === 'string' ? __BASE_PATH__.replace(/\/$/, '') : '';
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${siteOrigin()}${base}${normalized}`;
}

function absoluteImageUrl(image?: string) {
  if (!image) return siteUrl(DEFAULT_COVER);
  return /^https?:\/\//.test(image) ? image : siteUrl(image);
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export type SeoHeadSnapshot = {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  image?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

type HeadSink = (head: SeoHeadSnapshot) => void;
let headSink: HeadSink | null = null;

/**
 * 预渲染专用：renderToString 期间收集每个页面的 SEO 配置。
 * 浏览器端不设置 sink，此逻辑为无副作用的空操作。
 */
export function setSeoHeadSink(sink: HeadSink | null) {
  headSink = sink;
}

export function useSeo({ title, description, keywords, canonicalPath, image, noindex, jsonLd }: SeoOptions) {
  if (headSink) {
    headSink({ title, description, keywords, canonicalPath, image, noindex, jsonLd });
  }

  useEffect(() => {
    const url = siteUrl(canonicalPath);
    const cover = absoluteImageUrl(image);

    document.title = title;
    upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);
    upsertMeta(
      'name',
      'robots',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1',
    );
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:image', cover);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', cover);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    const usedIds: string[] = [];
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block, index) => {
        const id = `page-jsonld-${index}`;
        usedIds.push(id);
        let script = document.getElementById(id) as HTMLScriptElement | null;
        if (!script) {
          script = document.createElement('script');
          script.type = 'application/ld+json';
          script.id = id;
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(block);
      });
    }

    return () => {
      usedIds.forEach((id) => {
        const script = document.getElementById(id);
        if (script) script.remove();
      });
    };
  }, [title, description, keywords, canonicalPath, image, noindex, jsonLd]);
}

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    description:
      'แพลตฟอร์มให้ความรู้ Forex ภาษาไทยสำหรับมือใหม่ ตั้งแต่พื้นฐานจนถึงการเทรดและบริหารความเสี่ยง',
    url: siteUrl('/'),
    sameAs: [],
  };
}

export function faqPageSchema(items: { q: string; a: string; a2?: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: [item.a, item.a2].filter(Boolean).join(' '),
      },
    })),
  };
}

export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: siteUrl('/'),
    inLanguage: 'th-TH',
  };
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: siteUrl(item.path),
    })),
  };
}

type ArticleSchemaInput = {
  title: string;
  description: string;
  canonicalPath: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  category?: string;
  keywords?: string[];
};

export function articleSchema(input: ArticleSchemaInput) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: input.title,
    description: input.description,
    inLanguage: 'th-TH',
    url: siteUrl(input.canonicalPath),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': siteUrl(input.canonicalPath),
    },
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    image: absoluteImageUrl(input.image),
    ...(input.category ? { articleSection: input.category } : {}),
    ...(input.keywords && input.keywords.length > 0
      ? { keywords: input.keywords.join(', ') }
      : {}),
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: siteUrl('/'),
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: siteUrl('/'),
      logo: {
        '@type': 'ImageObject',
        url: siteUrl('/icon.png'),
      },
    },
  };
}